/**
 * AgentRoomGeralScreen — "Sala dos Agentes" GERAL (não presa a um projeto).
 *
 * Visão global e ao vivo do que cada agente está fazendo across TODAS as
 * execuções ativas/recentes do tenant. Read-only sobre timeline_events (via
 * GET /agents/activity, escopado por tenant pela RLS). Auto-refresh a cada 5s.
 *
 * Clicar num agente abre o Mission Control da execução de origem dele.
 */
import { C } from '@/theme/palette';
import { useEffect, useMemo, useState } from 'react';
import { useNavigationContext } from '@/navigation';
import { useAgentsActivity } from '@/api/agents';
import { AgentRoomView, custoDoAgente, textoDoCusto } from './missionControl/AgentRoom';
import { lugarNaSalaGeral } from '@/ui/office3d/officeModel';

// /agents/activity → estado e área no escritório. A regra mora no modelo (`lugarNaSalaGeral`):
// só fica na bancada quem trabalha ou concluiu há até 1 minuto.
const lugarDe = (a, agora) => lugarNaSalaGeral(
  { agentId: a.agent_id, active: a.active, execStatus: a.exec_status, occurredAt: a.occurred_at },
  agora);

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).
const STC = {
  completed: C.ok, running: C.accent, failed: C.bad, pending: C.warn,
  gate_waiting: C.warn, cancelled: C.muted,
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s atrás`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min atrás`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h atrás`;
  return `${Math.floor(h / 24)}d atrás`;
}

export function AgentRoomGeralScreen() {
  const { navigate } = useNavigationContext();
  const [activeOnly, setActiveOnly] = useState(false);
  const [view, setView] = useState('sala');
  const { data, isLoading } = useAgentsActivity({ activeOnly, refetchInterval: 5000 });

  const agents = data?.agents ?? [];
  const activeExecutions = data?.active_executions ?? 0;

  const openMC = (execId) => {
    if (execId) navigate(`/execution/mission-control?execution=${execId}`);
  };

  // adapta /agents/activity → o mesmo escritório do Mission Control (AgentRoomView)
  // o relógio da sala: quem concluiu há mais de 1 minuto sai da bancada mesmo que a API
  // devolva os mesmos dados (a consulta não muda, o tempo sim)
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  const roomAgents = useMemo(
    () => agents.map((a) => ({
      agentId: a.agent_id,
      name: a.agent_id || 'agente',
      ...lugarDe(a, agora),
      active: !!a.active,
      activity: a.message || null,
      // custo/tokens/turnos/duração (FR-17) — ausentes quando a API não os traz
      ...(custoDoAgente(a) || {}),
    })),
    [agents, agora],
  );
  const roomEvents = useMemo(
    () => agents
      .filter((a) => a.message)
      .map((a) => ({ occurred_at: a.occurred_at, agent_id: a.agent_id, message: a.message }))
      .sort((x, y) => new Date(x.occurred_at || 0) - new Date(y.occurred_at || 0)),
    [agents],
  );
  const execByAgent = useMemo(() => {
    const m = {};
    for (const a of agents) if (a.agent_id && a.execution_id) m[a.agent_id] = a.execution_id;
    return m;
  }, [agents]);
  const focusAgent = ({ id } = {}) => openMC(execByAgent[id]);

  return (
    <div className="sp-page" style={{ color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Sala dos Agentes</h2>
          <p style={{ color: C.dim, marginTop: 8, fontSize: 13.5 }}>
            O que cada agente está fazendo agora, em todas as execuções do tenant.
            Atualiza sozinho a cada 5s.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 999, overflow: 'hidden' }}>
            {[['sala', '🏢 Sala'], ['lista', '☰ Lista']].map(([v, lbl]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  border: 'none', cursor: 'pointer', padding: '5px 12px', fontSize: 12,
                  background: view === v ? C.accent : 'transparent',
                  color: view === v ? '#0A0A0A' : C.dim, fontWeight: view === v ? 700 : 500,
                }}
              >{lbl}</button>
            ))}
          </div>
          <span style={{ fontSize: 12.5, color: C.dim }}>
            <strong style={{ color: activeExecutions ? C.accent : C.muted }}>{activeExecutions}</strong> execução(ões) ativa(s)
            {' · '}
            <strong style={{ color: C.text }}>{agents.length}</strong> agente(s)
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: C.dim, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              style={{ accentColor: C.accent }}
            />
            Somente ativos
          </label>
        </div>
      </div>

      {isLoading && <div style={{ marginTop: 22, color: C.dim }}>Carregando…</div>}

      {!isLoading && agents.length === 0 && (
        <div style={{ marginTop: 24, padding: 28, border: `1px dashed ${C.border}`, borderRadius: 12,
          color: C.dim, textAlign: 'center' }}>
          {activeOnly
            ? 'Nenhum agente em execução ativa no momento.'
            : 'Nenhuma atividade de agente nas últimas 24h. Inicie um projeto para ver a sala ganhar vida.'}
        </div>
      )}

      {view === 'sala' && agents.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <AgentRoomView agents={roomAgents} events={roomEvents} onFocusAgent={focusAgent}
            rotuloDeAbrir="Abrir a execução do agente" />
          <p style={{ color: C.muted, fontSize: 11, marginTop: 8, fontFamily: 'monospace' }}>
            Clique num agente para abrir o Mission Control da execução dele.
          </p>
        </div>
      )}

      {view === 'lista' && agents.length > 0 && (
        <div style={{ marginTop: 20, display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {agents.map((a) => {
            const statusColor = a.active ? C.accent : C.muted;
            return (
              <button
                key={`${a.agent_id}-${a.execution_id}`}
                onClick={() => openMC(a.execution_id)}
                title="Abrir Mission Control desta execução"
                style={{
                  textAlign: 'left', cursor: a.execution_id ? 'pointer' : 'default',
                  background: C.surface, border: `1px solid ${a.active ? C.accent : C.border}`,
                  borderRadius: 12, padding: 14, color: C.text,
                  display: 'flex', flexDirection: 'column', gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{a.agent_id || '(agente)'}</span>
                  <span style={{
                    fontSize: 10.5, fontFamily: 'monospace', color: statusColor,
                    border: `1px solid ${statusColor}`, borderRadius: 999, padding: '2px 8px',
                  }}>
                    {a.active ? '● ativo' : 'ocioso'}
                  </span>
                </div>

                {a.phase && (
                  <span style={{ fontSize: 11.5, fontFamily: 'monospace', color: C.dim }}>
                    fase: {a.phase}
                  </span>
                )}

                {textoDoCusto(a) && (
                  <span data-testid="custo-do-agente"
                    style={{ fontSize: 11.5, fontFamily: 'monospace', color: C.dim }}>
                    {textoDoCusto(a)}
                  </span>
                )}

                <span style={{ fontSize: 12.5, color: C.text, lineHeight: 1.4,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {a.message || '—'}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 8, marginTop: 2, fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.project_name || '(sem projeto)'}
                    {a.exec_status ? ` · ${a.exec_status}` : ''}
                  </span>
                  <span style={{ flexShrink: 0 }}>{timeAgo(a.occurred_at)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
